import { ApiError, ApiErrorCode } from '@shared/infrastructure/http/api-error.js';

const GENERIC = 'No se ha podido completar la operación';

/**
 * What a person reads when an operation on a Virtual Profiler screen fails.
 *
 * Chosen by error **code**, never by the text the server sends. That matters more
 * here than anywhere else in the back-office: these codes come from Virtual
 * Profiler through the gateway, their original wording is in English, and some of
 * them say «conflict» for half a dozen unrelated reasons.
 *
 * Shared by the five screens on purpose. A run that cannot be cancelled means the
 * same thing whether you asked from the queue or from a group, and a second copy
 * of this table is a second chance to drift.
 */
export function feedbackFor(error: unknown): string {
  if (!(error instanceof ApiError)) return GENERIC;

  switch (error.code) {
    // The back-office is up; the tool behind it is not. Saying so is the whole
    // point: «no se ha podido completar» would send someone to look at the wrong
    // thing.
    case ApiErrorCode.VP_UNAVAILABLE:
      return 'Virtual Profiler no responde. Inténtalo en un momento.';

    // Runs.
    case ApiErrorCode.VP_RUN_NOT_FOUND:
      return 'Esa ejecución ya no existe: actualiza la pantalla para ver las que hay';
    case ApiErrorCode.VP_RUN_NOT_CANCELLABLE:
      return 'Esa ejecución ya había terminado: no hay nada que cancelar';
    // The distinction matters to the operator: waiting a few seconds works,
    // insisting does not.
    case ApiErrorCode.VP_RUN_PERSISTING:
      return 'La ejecución está guardando su resultado y no se puede cancelar ahora. Espera unos segundos.';
    case ApiErrorCode.VP_RUN_NOT_QUEUED:
      return 'Esa ejecución ya ha empezado: no se puede quitar de la cola, solo cancelar';
    // Every run older than the holding pen, 305's included, stored no job to repeat. Saying what
    // does work is the point: the operator came here to relaunch something.
    case ApiErrorCode.VP_RUN_NOT_REPLAYABLE:
      return 'Esa ejecución no guardó el trabajo que habría que repetir. Reanaliza el vídeo para lanzar uno nuevo.';
    // The button should have been hidden. Landing here means the flag changed, or
    // the runtime config was read before it did.
    case ApiErrorCode.VP_FORCE_CANCEL_DISABLED:
      return 'La cancelación forzada está desactivada en Virtual Profiler';

    // Analysis groups.
    case ApiErrorCode.VP_GROUP_NOT_FOUND:
      return 'Ese grupo de análisis ya no existe: actualiza la pantalla para ver los que hay';
    // The screens only ask for executions the history listed, so landing here means the history
    // on screen is out of date.
    case ApiErrorCode.VP_GROUP_BATCH_NOT_FOUND:
      return 'Este grupo no tiene ninguna ejecución con ese nombre: actualiza la pantalla para ver las que tiene';
    // Only a link can name a version the screen did not list.
    case ApiErrorCode.VP_DATASET_VERSION_NOT_FOUND:
      return 'Este grupo no tiene esa versión del dataset: se muestra la última';
    // The screen only offers «Generar versión» when something moved, so the dataset was versioned
    // meanwhile — by somebody else, or by a launch.
    case ApiErrorCode.VP_DATASET_UNCHANGED:
      return 'El dataset no ha cambiado desde su última versión: no hay nada que generar';
    // A group no longer holds anything an execution depends on, so upstream should not send this
    // any more; from an older Virtual Profiler it still says what to wait for.
    case ApiErrorCode.VP_GROUP_EXECUTION_RUNNING:
      return 'El grupo tiene una ejecución en marcha: espera a que termine o cancélala';
    // The screens only offer what the execution's state allows, so it moved under the screen: it
    // finished, was paused or resumed from elsewhere, or a run of it is still stopping. Upstream's
    // reason is in English; what helps is looking again.
    case ApiErrorCode.VP_EXECUTION_STATE_CONFLICT:
      return 'La ejecución ha cambiado mientras tanto y ya no admite eso: actualiza la pantalla para ver en qué punto está';
    case ApiErrorCode.VP_GROUP_NAME_TAKEN:
      return 'Ya hay un grupo de análisis con ese nombre';
    case ApiErrorCode.VP_GROUP_HAS_ACTIVE_RUNS:
      return 'El grupo tiene análisis en marcha. Espera a que terminen o cancélalos primero.';
    case ApiErrorCode.VP_GROUP_CONFIG_MISSING:
      return 'La configuración elegida se ha borrado: elige otra para lanzar la ejecución';
    // One code for every cause, a control run's included: none of its videos has yet what its kind
    // needs. Upstream tells them apart only in prose, so the sentence names them all.
    case ApiErrorCode.VP_GROUP_RUN_INVALID:
      return 'Virtual Profiler no puede analizar el grupo así: no le queda ningún vídeo que analizar (en un grupo de control, ninguno tiene aún lo que necesita), o la configuración elegida nunca se guardó con motores';

    // Analysis configurations.
    case ApiErrorCode.VP_CONFIG_NOT_FOUND:
      return 'Esa configuración de análisis ya no existe';
    case ApiErrorCode.VP_CONFIG_NAME_TAKEN:
      return 'Ya hay una configuración de análisis con ese nombre';
    case ApiErrorCode.VP_CONFIG_IN_USE:
      return 'Esa configuración ya se ha usado en algún análisis y no se puede borrar';
    case ApiErrorCode.VP_CONFIG_BASE_UNAVAILABLE:
      return 'No se puede partir de Estándar: el catálogo del motor no está completo. No se ha creado nada.';
    // One code for two refusals — copying it and activating it — and the reason is the same.
    case ApiErrorCode.VP_CONFIG_WITHOUT_ENGINES:
      return 'Esa configuración nunca se guardó con motores: no se puede copiar ni activar';
    case ApiErrorCode.VP_CONFIG_ACTIVE:
      return 'Es la configuración activa: activa otra antes de borrarla';

    // The video dataset.
    case ApiErrorCode.VP_VIDEO_NOT_FOUND:
      return 'Ese vídeo ya no existe';
    // One code for three causes upstream does not tell apart, so the sentence names all three:
    // the operator can check which from the queue.
    case ApiErrorCode.VP_VIDEO_BUSY:
      return 'El vídeo no admite otro análisis ahora: se está subiendo o analizando, ya tiene uno en marcha, o ha llegado al máximo de ejecuciones. Míralo en la cola.';
    // Only reachable if this build and Virtual Profiler disagree about the engines, which is why
    // it asks for a reload rather than for a different choice.
    case ApiErrorCode.VP_ENGINE_SELECTION_INVALID:
      return 'Virtual Profiler no acepta esa combinación de motores. Recarga la página y vuelve a elegirlos.';
    case ApiErrorCode.VP_ANALYSIS_CONFIG_REQUIRED:
      return 'Hace falta una configuración de análisis guardada con motores: el vídeo no tiene ninguna, o la elegida (o su versión) no se puede usar';
    // Upstream goes on launching after we stop waiting. Relaunching now would pay twice for the
    // same analyses, so the sentence sends the operator to where the answer is.
    case ApiErrorCode.VP_LAUNCH_UNCONFIRMED:
      return 'Virtual Profiler no ha confirmado el lanzamiento a tiempo y puede que los análisis ya estén en cola. Míralo en la cola antes de volver a lanzarlos.';
    // Says which way out there is, because the other two variants do work: an
    // «operación no permitida» here would leave the person with nothing to try.
    case ApiErrorCode.VP_DELETE_VARIANT_UNAVAILABLE:
      return 'No se puede borrar el análisis de un vídeo que está en un grupo con ejecuciones: es su histórico. Puedes borrar el fichero y conservar el análisis, o excluirlo de las métricas.';

    // Gesture labels and control groups. The panel checks a label before sending it, so a refusal
    // means the catalogue or the labels changed under the screen: reloading is the way out.
    case ApiErrorCode.VP_GESTURE_LABEL_NOT_FOUND:
      return 'Esa etiqueta ya no existe: recarga la página para ver las que hay';
    case ApiErrorCode.VP_GESTURE_LABEL_INVALID:
      return 'Virtual Profiler no acepta esa etiqueta: el gesto no se puede etiquetar o los tiempos no son válidos. Recarga la página y vuelve a intentarlo.';
    case ApiErrorCode.VP_INCOHERENCE_RANGE_NOT_FOUND:
      return 'Ese tramo de mentira ya no existe: recarga la página para ver los que hay';
    case ApiErrorCode.VP_INCOHERENCE_RANGE_INVALID:
      return 'Virtual Profiler no acepta ese tramo: el final tiene que ir después del principio.';
    case ApiErrorCode.VP_CONTROL_KIND_UNKNOWN:
      return 'Virtual Profiler no reconoce ese grupo de control. Recarga la página.';
    // Somebody else asked first, or the job finished meanwhile: the panel catches up by itself.
    case ApiErrorCode.VP_TRANSCRIPTION_BUSY:
      return 'La transcripción de este vídeo ya está en marcha o terminada';

    // The people of the dataset.
    case ApiErrorCode.VP_PERSON_NOT_FOUND:
      return 'Esa persona ya no existe: actualiza la pantalla para ver las que hay';
    case ApiErrorCode.VP_PERSON_NAME_TAKEN:
      return 'Ya hay una persona con ese nombre';
    case ApiErrorCode.VP_CV_BUSY:
      return 'Ese CV se está analizando: espera a que termine para borrarlo';
    case ApiErrorCode.VP_CV_NOT_EDITABLE:
      return 'Ese CV todavía no tiene datos extraídos que editar';
    case ApiErrorCode.VP_PERSON_BUSY:
      return 'Esa persona tiene un CV o un vídeo en proceso: espera a que termine para borrarla';

    // «Simular» (Metodología).
    case ApiErrorCode.VP_SIMULATION_NOT_FOUND:
      return 'Esa simulación ya no existe: duran 24 horas. Vuelve a simular';
    case ApiErrorCode.VP_SIMULATION_CONFIG_INCOMPLETE:
      return 'El motor no puede simular esta configuración: le falta algo. Revisa la lista de lo que falta';
    case ApiErrorCode.VP_SIMULATION_NO_ANALYSIS:
      return 'Este vídeo no tiene un análisis con ideas sobre el que simular: analízalo con veracidad primero';
    case ApiErrorCode.VP_SIMULATION_IN_PROGRESS:
      return 'Este vídeo ya tiene una simulación en marcha: espera a que termine';

    // Shared with the rest of the back-office. Repeated here rather than
    // delegated: every screen that catches an error needs all of them, and one
    // switch is easier to read than two.
    case ApiErrorCode.PERMISSION_DENIED:
      return 'Ya no tienes permiso para esto. Vuelve a entrar para actualizar tus permisos.';
    case ApiErrorCode.VALIDATION_FAILED:
      return 'Hay algún dato que no es válido: revísalo y vuelve a intentarlo';
    case ApiErrorCode.NETWORK_ERROR:
      return 'No hay conexión con el servidor';
    // Not a failure, and it deliberately says so without an excuse: whoever
    // cancelled the upload knows why it stopped.
    case ApiErrorCode.UPLOAD_CANCELLED:
      return 'Subida cancelada';
    default:
      return GENERIC;
  }
}
